import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPinned, Send, Trash2, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, fmtDate, numBR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export default function Market() {
  const { company } = useAuth();
  const [categories, setCategories] = useState([]);
  const [mine, setMine] = useState([]);
  const [consult, setConsult] = useState({ state: company.state || "SP", city: company.city || "", category: "hora_tecnica", capacity: "" });
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ state: company.state || "", city: company.city || "", region: "", category: "hora_tecnica", capacity: "", equipment_type: "", value: "", reference_date: "", notes: "" });
  const [accept, setAccept] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/market/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/market/contributions/mine").then((r) => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const capsOf = (catId) => categories.find((c) => c.id === catId)?.capacities || [];

  const doConsult = async () => {
    if (!consult.state || !consult.city) {
      toast.error("Informe estado e cidade.");
      return;
    }
    try {
      const { data } = await api.get("/market/reference", { params: consult });
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) {
      toast.error("É necessário aceitar os Termos de Contribuição de Dados.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/market/contributions", { ...form, value: numBR(form.value), accept_terms: accept });
      toast.success(data.message);
      setForm({ ...form, value: "", notes: "", equipment_type: "" });
      setAccept(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir sua contribuição? As estatísticas agregadas serão recalculadas.")) return;
    await api.delete(`/market/contributions/${id}`);
    toast.success("Contribuição excluída.");
    load();
  };

  return (
    <div className="space-y-5 animate-in-up max-w-3xl" data-testid="market-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            Preços regionais <Badge className="bg-amber-100 text-amber-700 border-amber-200">BETA</Badge>
          </h1>
          <p className="text-sm text-slate-500">Base colaborativa de referências de mercado</p>
        </div>
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 flex gap-3 text-sm text-sky-900" data-testid="market-disclaimer">
        <Info className="h-5 w-5 shrink-0 mt-0.5" />
        <p>
          As referências são estatísticas agregadas e anonimizadas (mediana e média aparada), formadas por
          contribuições de profissionais participantes. Não são tabela obrigatória de preços, não identificam
          nenhum profissional e a decisão final é sempre sua. Consulte os{" "}
          <Link to="/termos?aba=contribuicao" className="underline font-medium">Termos de Contribuição</Link>.
        </p>
      </div>

      <Tabs defaultValue="consultar">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="consultar" data-testid="market-tab-consult">Consultar referência</TabsTrigger>
          <TabsTrigger value="contribuir" data-testid="market-tab-contribute">Contribuir</TabsTrigger>
          <TabsTrigger value="minhas" data-testid="market-tab-mine">Minhas contribuições ({mine.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="consultar" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Estado</Label>
                  <Select value={consult.state} onValueChange={(v) => setConsult({ ...consult, state: v })}>
                    <SelectTrigger className="h-12" data-testid="mk-state"><SelectValue /></SelectTrigger>
                    <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Cidade</Label>
                  <Input className="h-12" data-testid="mk-city" value={consult.city} onChange={(e) => setConsult({ ...consult, city: e.target.value })} placeholder="São Paulo" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tipo de serviço</Label>
                  <Select value={consult.category} onValueChange={(v) => setConsult({ ...consult, category: v, capacity: "" })}>
                    <SelectTrigger className="h-12" data-testid="mk-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {capsOf(consult.category).length > 0 && (
                  <div className="space-y-1.5">
                    <Label>Capacidade</Label>
                    <Select value={consult.capacity} onValueChange={(v) => setConsult({ ...consult, capacity: v })}>
                      <SelectTrigger className="h-12" data-testid="mk-capacity"><SelectValue placeholder="BTUs" /></SelectTrigger>
                      <SelectContent>{capsOf(consult.category).map((cap) => <SelectItem key={cap} value={cap}>{cap} BTUs</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={doConsult} data-testid="mk-consult-btn">
                <MapPinned className="h-4 w-4 mr-2" /> Consultar referência
              </Button>

              {result && (
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="mk-result">
                  {result.forming ? (
                    <div>
                      <p className="font-semibold text-amber-800">Referência em formação</p>
                      <p className="text-sm text-slate-600 mt-1">{result.message}</p>
                      <p className="text-sm mt-2">Piso padrão (parâmetro do administrador): <b className="font-mono-num">{brl(result.piso_padrao)}</b></p>
                      <p className="text-xs text-slate-500 mt-1">{result.count} de {result.min_contributions} contribuições mínimas.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="font-semibold">{result.category?.name} — {consult.city}/{consult.state}</p>
                      <div className="flex flex-wrap gap-6">
                        <div>
                          <p className="text-xs text-slate-500">Faixa recomendada</p>
                          <p className="font-mono-num text-xl font-semibold">{brl(result.range[0])} – {brl(result.range[1])}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Mediana</p>
                          <p className="font-mono-num text-xl font-semibold text-sky-700">{brl(result.median)}</p>
                        </div>
                        {!result.limited && result.trimmed_mean != null && (
                          <div>
                            <p className="text-xs text-slate-500">Média aparada</p>
                            <p className="font-mono-num text-xl font-semibold">{brl(result.trimmed_mean)}</p>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {result.count} contribuições · confiança {result.confidence} · atualizada em {result.updated_at}
                        {result.demo && " · dados de demonstração"}
                      </p>
                      {result.limited && (
                        <p className="text-xs text-amber-700">Plano Grátis: estatísticas detalhadas disponíveis no plano Profissional.</p>
                      )}
                      <p className="text-xs text-slate-500 italic">{result.disclaimer}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contribuir" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Contribuir com um preço praticado</CardTitle>
              <p className="text-xs text-slate-500">
                Sua contribuição é anonimizada e agregada. Ninguém vê seu valor individual. Você pode corrigir ou excluir depois.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Estado *</Label>
                    <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })} required>
                      <SelectTrigger className="h-12" data-testid="mk-c-state"><SelectValue placeholder="UF" /></SelectTrigger>
                      <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Cidade *</Label>
                    <Input className="h-12" data-testid="mk-c-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Tipo de serviço *</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, capacity: "" })}>
                      <SelectTrigger className="h-12" data-testid="mk-c-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {capsOf(form.category).length > 0 && (
                    <div className="space-y-1.5">
                      <Label>Capacidade</Label>
                      <Select value={form.capacity} onValueChange={(v) => setForm({ ...form, capacity: v })}>
                        <SelectTrigger className="h-12" data-testid="mk-c-capacity"><SelectValue placeholder="BTUs" /></SelectTrigger>
                        <SelectContent>{capsOf(form.category).map((cap) => <SelectItem key={cap} value={cap}>{cap} BTUs</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Marca/modelo do equipamento (opcional)</Label>
                  <Input className="h-12" data-testid="mk-c-equipment" value={form.equipment_type} onChange={(e) => setForm({ ...form, equipment_type: e.target.value })} placeholder="Ex: Springer Midea 12.000 BTUs" />
                  <p className="text-xs text-slate-500">Serve para qualquer serviço (manutenção, carga de gás, visita etc.), não só instalação ou limpeza.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Valor praticado (R$) *</Label>
                    <Input className="h-12 font-mono-num" type="text" inputMode="decimal" required placeholder="Ex: 350,00" data-testid="mk-c-value"
                           value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Data de referência</Label>
                    <Input className="h-12" type="date" data-testid="mk-c-date" value={form.reference_date} onChange={(e) => setForm({ ...form, reference_date: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Observações (opcional)</Label>
                  <Textarea rows={2} data-testid="mk-c-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="flex items-start gap-2.5">
                  <Checkbox id="contrib-terms" checked={accept} onCheckedChange={setAccept} className="mt-0.5" data-testid="mk-c-terms" />
                  <Label htmlFor="contrib-terms" className="text-sm text-slate-600 font-normal leading-snug">
                    Aceito os <Link to="/termos?aba=contribuicao" className="text-sky-700 underline" target="_blank">Termos de Contribuição de Dados</Link> e
                    confirmo que o valor informado é real e praticado por mim. Entendo que os dados serão agregados e anonimizados.
                  </Label>
                </div>
                <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="mk-c-submit-btn">
                  <Send className="h-4 w-4 mr-2" /> {sending ? "Enviando..." : "Enviar contribuição"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="minhas" className="mt-4 space-y-2">
          {mine.length === 0 && <p className="text-sm text-slate-500">Você ainda não contribuiu. Que tal ser o primeiro da sua região?</p>}
          {mine.map((c) => (
            <Card key={c.id} className="border-slate-200" data-testid={`my-contrib-${c.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{c.service_name} {c.capacity && `· ${c.capacity} BTUs`}{c.equipment_type && ` · ${c.equipment_type}`}</p>
                  <p className="text-xs text-slate-500">{c.city}/{c.state} · {fmtDate(c.created_at)}</p>
                  {c.status === "flagged" && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 mt-1">Em revisão</Badge>}
                  {c.status === "rejected" && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 mt-1">Rejeitada</Badge>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono-num font-semibold">{brl(c.value)}</span>
                  <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 hover:bg-red-50" onClick={() => remove(c.id)} data-testid={`my-contrib-del-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
